import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, currencyApi, summaryApi } from '../lib/api';
import type { Asset, AssetBalanceHistory, AssetType, CustomCurrency } from '@asset-mgmt/shared';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const TYPE_LABELS: Record<AssetType, string> = {
  cash:         'Cash',
  bank_account: 'Bank Account',
  bank_saving:  'Bank Saving',
  investment:   'Investment',
  crypto:       'Crypto',
  real_estate:  'Real Estate',
  liability:    'Liability',
  gold:         'Gold',
  silver:       'Silver',
  custom:       'Custom',
};

const TYPE_COLORS: Record<AssetType, string> = {
  cash:         'bg-green-100 text-green-700',
  bank_account: 'bg-blue-100 text-blue-700',
  bank_saving:  'bg-teal-100 text-teal-700',
  investment:   'bg-purple-100 text-purple-700',
  crypto:       'bg-orange-100 text-orange-700',
  real_estate:  'bg-yellow-100 text-yellow-700',
  liability:    'bg-red-100 text-red-700',
  gold:         'bg-amber-100 text-amber-700',
  silver:       'bg-slate-100 text-slate-600',
  custom:       'bg-gray-100 text-gray-700',
};

export default function Assets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const currency = user?.settings.baseCurrency ?? 'VND';
  const isViewer = user?.role === 'viewer';

  const [assets, setAssets] = useState<Asset[]>([]);
  const [netWorth, setNetWorth] = useState<number>(0);
  const [assetValues, setAssetValues] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  function reload() {
    setLoading(true);
    Promise.all([api.get<Asset[]>('/assets'), summaryApi.netWorth(), summaryApi.assetHistory()])
      .then(([a, nw, ah]) => {
        setAssets(a);
        setNetWorth(nw[nw.length - 1]?.netWorth ?? 0);
        // Build assetId → current base-currency value from the last history point
        const map = new Map<string, number>();
        for (const entry of ah as AssetBalanceHistory[]) {
          const last = entry.points[entry.points.length - 1];
          if (last != null) map.set(entry.assetId, last.balance);
        }
        setAssetValues(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(reload, []);

  async function handleDelete(id: string) {
    if (!confirm('Delete this asset?')) return;
    try {
      await api.delete(`/assets/${id}`);
      toast.success('Asset deleted.');
      reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete asset.');
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Assets</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Net worth:{' '}
            <span className={netWorth >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-500 font-semibold'}>
              {fmt(netWorth, currency)}
            </span>
          </p>
        </div>
        <button
          onClick={() => !isViewer && setShowForm(true)}
          disabled={isViewer}
          title={isViewer ? 'Viewers cannot add assets' : undefined}
          className="hidden items-center gap-1.5 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
        >
          <span>+</span>
          Add Asset
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-600 border-t-transparent" />
        </div>
      ) : assets.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50/80 dark:bg-gray-900/40">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3 text-right">Currency</th>
                  <th className="px-4 py-3 text-right">Value ({currency})</th>
                  <th className="px-4 py-3 text-right">Share</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {assets.map((a) => (
                  <tr key={a.id} className="group transition-colors hover:bg-primary-50/40 dark:hover:bg-gray-700/30 cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>
                    <td className="px-4 py-3.5 font-medium text-gray-900 dark:text-gray-100">
                      {a.name}
                      {a.isLiability && (
                        <span className="ml-2 text-xs text-red-500">(liability)</span>
                      )}
                      {a.sharedBy && (
                        <p className="text-xs font-normal text-violet-500 dark:text-violet-400">Shared by {a.sharedBy}</p>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TYPE_COLORS[a.type]}`}>
                        {TYPE_LABELS[a.type]}
                      </span>
                    </td>
                    <td className={`px-4 py-3.5 text-right font-mono tabular-nums ${a.isLiability ? 'text-red-500' : 'text-gray-900 dark:text-gray-100'}`}>
                      {a.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3.5 text-right text-gray-500 dark:text-gray-400">{a.type === 'gold' ? 'chỉ' : a.type === 'silver' ? 'lượng' : a.currency}</td>
                    <td className={`px-4 py-3.5 text-right font-mono tabular-nums text-sm font-medium ${
                      (assetValues.get(a.id) ?? 0) < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'
                    }`}>
                      {fmt(assetValues.get(a.id) ?? 0, currency)}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      {netWorth !== 0 && assetValues.has(a.id) ? (
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
                          (assetValues.get(a.id)! / Math.abs(netWorth)) >= 0
                            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                            : 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {((assetValues.get(a.id)! / Math.abs(netWorth)) * 100).toFixed(1)}%
                        </span>
                      ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleDelete(a.id); }}
                        disabled={isViewer}
                        title={isViewer ? 'Viewers cannot delete assets' : undefined}
                        className="rounded-lg px-2 py-1 text-xs text-red-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-2">
            {assets.map((a) => (
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/assets/${a.id}`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/assets/${a.id}`)}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 shadow-sm cursor-pointer hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">{a.name}</span>
                      {a.isLiability && (
                        <span className="text-xs text-red-500">(liability)</span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[a.type]}`}>
                        {TYPE_LABELS[a.type]}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{a.type === 'gold' ? 'chỉ' : a.type === 'silver' ? 'lượng' : a.currency}</span>
                      {a.sharedBy && (
                        <span className="text-xs text-violet-500 dark:text-violet-400">Shared by {a.sharedBy}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`font-mono text-sm font-semibold tabular-nums ${a.isLiability ? 'text-red-500' : 'text-gray-900 dark:text-gray-100'}`}>
                      {a.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} {a.type === 'gold' ? 'chỉ' : a.type === 'silver' ? 'lượng' : a.currency}
                    </span>
                    {assetValues.has(a.id) && (
                      <span className={`font-mono text-xs tabular-nums ${
                        (assetValues.get(a.id) ?? 0) < 0 ? 'text-red-400 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {fmt(assetValues.get(a.id) ?? 0, currency)}
                        {netWorth !== 0 && (
                          <span className="ml-1 text-gray-400 dark:text-gray-500">
                            ({((( assetValues.get(a.id)! / Math.abs(netWorth)) * 100)).toFixed(1)}%)
                          </span>
                        )}
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); void handleDelete(a.id); }}
                      disabled={isViewer}
                      title={isViewer ? 'Viewers cannot delete assets' : undefined}
                      className="rounded-lg px-2 py-1 text-xs font-medium text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showForm && <AssetForm onClose={() => { setShowForm(false); reload(); }} />}

      {/* FAB — always visible, above bottom tabs on mobile */}
      <button
        onClick={() => !isViewer && setShowForm(true)}
        disabled={isViewer}
        title={isViewer ? 'Viewers cannot add assets' : undefined}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg ring-4 ring-primary-600/20 transition-transform hover:bg-primary-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
        aria-label="Add asset"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
}

function AssetForm({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: '', type: 'bank_account' as AssetType,
    currency: 'VND', balance: '', isLiability: false, description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [standardCurrencies, setStandardCurrencies] = useState<string[]>([]);
  const [customCurrencies,   setCustomCurrencies]   = useState<CustomCurrency[]>([]);

  useEffect(() => {
    Promise.all([currencyApi.listStandard(), currencyApi.listCustom()])
      .then(([std, custom]) => { setStandardCurrencies(std); setCustomCurrencies(custom); })
      .catch(() => {});
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);

    // Resolve custom currency → ISO before sending so no custom codes are ever stored
    const rawBalance  = parseFloat(form.balance) || 0;
    const customMatch = customCurrencies.find((c) => c.code === form.currency);
    const finalBalance  = customMatch ? rawBalance * customMatch.multiplier : rawBalance;
    const finalCurrency = customMatch ? customMatch.baseCode : form.currency;

    try {
      await api.post('/assets', { ...form, balance: finalBalance, currency: finalCurrency });
      toast.success('Asset created.');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create asset.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">New Asset</h2>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
          <Field label="Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
          </Field>
          <Field label="Type">
            <select
              value={form.type}
              onChange={(e) => {
                const newType = e.target.value as AssetType;
                setForm({
                  ...form,
                  type: newType,
                  // Gold/silver assets always use VND as their cost currency
                  currency: newType === 'gold' || newType === 'silver' ? 'VND' : form.currency,
                });
              }}
              className={input}
            >
              {(Object.keys(TYPE_LABELS) as AssetType[]).map((t) => (
                <option key={t} value={t}>{TYPE_LABELS[t]}</option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={form.type === 'gold' ? 'Initial Quantity (chỉ)' : form.type === 'silver' ? 'Initial Quantity (lượng)' : 'Balance'}>
              <input type="number" step="any" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className={input} />
            </Field>
            {form.type !== 'gold' && form.type !== 'silver' && (
            <Field label="Currency">
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={input}>
                {!standardCurrencies.includes(form.currency) &&
                 !customCurrencies.find((c) => c.code === form.currency) && (
                  <option value={form.currency}>{form.currency}</option>
                )}
                {customCurrencies.length > 0 && (
                  <optgroup label="Custom">
                    {customCurrencies.map((c) => <option key={c.id} value={c.code}>{c.code} — {c.name}</option>)}
                  </optgroup>
                )}
                {standardCurrencies.length > 0 && (
                  <optgroup label="Standard">
                    {standardCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
            )}
          </div>
          <Field label="Description (optional)">
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isLiability} onChange={(e) => setForm({ ...form, isLiability: e.target.checked })} />
            This is a liability / debt
          </label>
          <div className="flex gap-3 pt-2">
            {error && (
              <p className="col-span-2 text-sm text-red-500">{error}</p>
            )}
            <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 rounded-lg bg-primary-600 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{label}</span>
      {children}
    </label>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-16 text-center">
      <p className="text-gray-500 dark:text-gray-400">No assets yet.</p>
      <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">Click "+ Add Asset" to get started.</p>
    </div>
  );
}

function fmt(v: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
}

const input = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none placeholder:text-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900/30 dark:[color-scheme:dark]';
